import { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AppShell } from '@/components/AppShell';
import { RequireAuth } from '@/components/RequireAuth';
import SignInPage from '@/pages/SignInPage';
import PrivacyPage from '@/pages/PrivacyPage';
import SupportPage from '@/pages/SupportPage';
import CompleteProfilePage from '@/pages/CompleteProfilePage';
import StudentHomePage from '@/pages/StudentHomePage';
import RequestMuqriPage from '@/pages/RequestMuqriPage';
import WaitingRoomPage from '@/pages/WaitingRoomPage';
import MuqriSessionPage from '@/pages/MuqriSessionPage';
import UnauthorizedPage from '@/pages/UnauthorizedPage';
import RequestsPage from '@/pages/admin/RequestsPage';
import UsersPage from '@/pages/admin/UsersPage';
import SessionsPage from '@/pages/admin/SessionsPage';
import StatsPage from '@/pages/admin/StatsPage';
import AdminTracksPage from '@/pages/admin/TracksPage';
import ChildrenPage from '@/pages/ChildrenPage';
import ChildProgressPage from '@/pages/ChildProgressPage';
import CurriculumPage from '@/pages/CurriculumPage';
import AdminCurriculaPage from '@/pages/admin/CurriculaPage';
import ProfilePage from '@/pages/ProfilePage';
import TracksPage from '@/pages/TracksPage';
import TrackPage from '@/pages/TrackPage';

/**
 * Lazy on purpose: the printed mushaf's layout is 1.7 MB of page geometry,
 * and only the call needs it. Loading it up front would make every other
 * page pay for a screen most visits never open.
 */
const CallPage = lazy(() => import('@/pages/CallPage'));
const LabPage = lazy(() => import('@/pages/LabPage'));
const AdminSchoolsPage = lazy(() => import('@/pages/admin/SchoolsPage'));
const MapPage = lazy(() => import('@/pages/MapPage'));
const RewardsPage = lazy(() => import('@/pages/RewardsPage'));
const BoardPage = lazy(() => import('@/pages/BoardPage'));
const AdminGamePage = lazy(() => import('@/pages/admin/GamePage'));
const PracticePage = lazy(() => import('@/pages/PracticePage'));
const CertificatesPage = lazy(() => import('@/pages/CertificatesPage'));
const ClaimGuestPage = lazy(() => import('@/pages/ClaimGuestPage'));

/**
 * The root is sign-in for a visitor and a redirect for anyone already signed
 * in, so a returning user never sees the phone form flash before being moved
 * on — which means WAITING for the stored session to be read rather than
 * reading 'not yet known' as 'signed out'. A signed-in user with no name yet
 * goes to the profile step: joining a queue writes that name, and the rules
 * refuse an empty one.
 */
function Root() {
  const { identity, restoring, resolvingName } = useAuth();
  if (restoring) return <div data-testid="auth-restoring" />;
  if (!identity) return <SignInPage />;
  // Waiting on the NAME, not only on the session. An account created by
  // verifyOtpAndSignIn has an empty Auth displayName, so redirecting on that
  // emptiness sends a returning reader to a step they finished long ago —
  // and nothing sends them back off it.
  if (resolvingName) return <div data-testid="auth-restoring" />;
  if (!identity.displayName) return <Navigate to="/complete-profile" replace />;
  return <Navigate to="/home" replace />;
}

/**
 * The profile step, which nobody who already has a name should be sitting on.
 *
 * The second half of the same bug: even once the name arrived, the reader
 * stayed on this page because reaching it is a redirect and leaving it was
 * nobody's job.
 */
function ProfileStep() {
  const { identity, restoring, resolvingName } = useAuth();
  if (restoring || resolvingName) return <div data-testid="auth-restoring" />;
  if (!identity) return <Navigate to="/" replace />;
  if (identity.displayName) return <Navigate to="/home" replace />;
  return <CompleteProfilePage />;
}

/** Every signed-in page shares the shell; the sign-in and profile steps do not. */
function Shell({ role, children }: { role?: 'muqri' | 'admin'; children: React.ReactNode }) {
  return (
    <RequireAuth role={role}>
      <AppShell>{children}</AppShell>
    </RequireAuth>
  );
}

/**
 * A way in for the emulator, and ONLY for the emulator.
 *
 * `?devToken=…` signs in with a custom token minted by the local Auth
 * emulator, so the whole app can be walked without an OTP. Both guards from
 * config.web.ts apply again here, so this component is dropped from every
 * production bundle rather than merely being unreachable in one.
 */
function DevSignIn() {
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (!(import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === '1')) return;
    const token = new URLSearchParams(window.location.search).get('devToken');
    if (!token || done) return;
    setDone(true);
    void import('firebase/auth').then(async ({ getAuth, signInWithCustomToken }) => {
      const { app } = await import('@core/firebase/config');
      await signInWithCustomToken(getAuth(app), token);
      window.history.replaceState({}, '', window.location.pathname);
    });
  }, [done]);
  return null;
}

export default function App() {
  return (
    <>
    <DevSignIn />
    <Routes>
      <Route path="/" element={<Root />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      {/*
        * PUBLIC on purpose, and outside the shell: both stores require a
        * privacy policy and a support page that a reviewer can open without
        * an account, and a page behind sign-in fails that check.
        */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route
        path="/complete-profile"
        element={<RequireAuth><ProfileStep /></RequireAuth>}
      />

      <Route path="/home" element={<Shell><StudentHomePage /></Shell>} />
      <Route path="/request-muqri" element={<Shell><RequestMuqriPage /></Shell>} />
      <Route path="/profile" element={<Shell><ProfilePage /></Shell>} />
      <Route path="/children" element={<Shell><ChildrenPage /></Shell>} />
      <Route path="/children/:childUid" element={<Shell><ChildProgressPage /></Shell>} />
      <Route path="/curriculum" element={<Shell><CurriculumPage /></Shell>} />
      <Route path="/tracks" element={<Shell><TracksPage /></Shell>} />
      {/* المرحلة 5 — the map, the shelf and the board. */}
      <Route
        path="/map"
        element={<Shell><Suspense fallback={<div data-testid="map-loading" />}><MapPage /></Suspense></Shell>}
      />
      <Route
        path="/rewards"
        element={<Shell><Suspense fallback={<div data-testid="rewards-loading" />}><RewardsPage /></Suspense></Shell>}
      />
      <Route
        path="/board"
        element={<Shell><Suspense fallback={<div data-testid="board-loading" />}><BoardPage /></Suspense></Shell>}
      />
      {/* A guest keeping the account they already have. */}
      <Route
        path="/claim"
        element={<Shell><Suspense fallback={<div data-testid="claim-loading" />}><ClaimGuestPage /></Suspense></Shell>}
      />
      {/* شهاداتي — one per finished surah, issued by the server. */}
      <Route
        path="/certificates"
        element={<Shell><Suspense fallback={<div data-testid="certificates-loading" />}><CertificatesPage /></Suspense></Shell>}
      />
      {/* المرحلة 6 — التدريب الذاتي. */}
      <Route
        path="/practice"
        element={<Shell><Suspense fallback={<div data-testid="practice-loading" />}><PracticePage /></Suspense></Shell>}
      />
      {/* المعمل — the class grid, for the مُقرئ taking that class. */}
      <Route path="/lab" element={<Shell role="muqri"><LabPage /></Shell>} />
      {/* المدارس — an admin founds one, a manager fills it. */}
      <Route path="/admin/schools" element={<Shell role="admin"><AdminSchoolsPage /></Shell>} />
      <Route path="/tracks/:trackId" element={<Shell><TrackPage /></Shell>} />
      <Route path="/waiting/:sessionId" element={<Shell><WaitingRoomPage /></Shell>} />
      <Route path="/session/today" element={<Shell role="muqri"><MuqriSessionPage /></Shell>} />
      <Route
        path="/call/:sessionId"
        element={(
          <Shell>
            <Suspense fallback={<div data-testid="call-loading" />}>
              <CallPage />
            </Suspense>
          </Shell>
        )}
      />

      <Route path="/admin/requests" element={<Shell role="admin"><RequestsPage /></Shell>} />
      <Route path="/admin/users" element={<Shell role="admin"><UsersPage /></Shell>} />
      <Route path="/admin/sessions" element={<Shell role="admin"><SessionsPage /></Shell>} />
      <Route path="/admin/stats" element={<Shell role="admin"><StatsPage /></Shell>} />
      <Route path="/admin/curricula" element={<Shell role="admin"><AdminCurriculaPage /></Shell>} />
      <Route path="/admin/tracks" element={<Shell role="admin"><AdminTracksPage /></Shell>} />
      <Route
        path="/admin/game"
        element={(
          <Shell role="admin">
            <Suspense fallback={<div data-testid="admin-game-loading" />}><AdminGamePage /></Suspense>
          </Shell>
        )}
      />

      {/* An unknown path is a typo, not an error page: send it home. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
